import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listenMapAdCampaigns, listenMapAdRuntime, saveMapAdDraft, publishMapAd,
  setMapAdCampaignState, setMapAdsEnabled, uploadMapAdImage, mapAdImageUrl,
  listenPlatformConfig,
} from '../services/firebase';
import { useLang } from '../lang/LangContext';
import './MapAds.css';

const EMPTY = {
  name: '', sponsor: '', headline: '', productUrl: '',
  // Book screen by default; the auto tag needs Approaching pickup or During trip.
  topEnabled: true, tagEnabled: false,
  homeEnabled: true, searchingEnabled: false, pickupEnabled: false, tripEnabled: false,
  allTowns: true, townIds: [],
  topPath: '', tagPath: '', startAt: '', endAt: '',
};

const SCREENS = [
  ['homeEnabled', 'Book (home) screen'],
  ['searchingEnabled', 'Searching for an Anna'],
  ['pickupEnabled', 'Approaching pickup (banner + auto tag)'],
  ['tripEnabled', 'During trip (banner + auto tag)'],
];

// Mirrors the server's validateDraft defaults so an older saved draft doesn't
// look "changed" the moment it is opened.
function formFromDraft(draft = {}) {
  return {
    ...EMPTY, homeEnabled: false, searchingEnabled: false, ...draft,
    pickupEnabled: draft.pickupEnabled !== false,
    allTowns: draft.allTowns !== false,
    townIds: Array.isArray(draft.townIds) ? draft.townIds : [],
    startAt: istInput(draft.startAt), endAt: istInput(draft.endAt),
  };
}

// 'default' is the main town (mapCenter + serviceRadiusKm); extra towns come
// from config/platform.zones, with the same ids rides store as zoneId.
export function townOptions(platform) {
  const zones = Array.isArray(platform?.zones) ? platform.zones : [];
  return [
    { id: 'default', name: 'Piler (main town)', active: true },
    ...zones.filter((zone) => zone && zone.id).map((zone) => ({
      id: String(zone.id), name: zone.name || String(zone.id), active: zone.active !== false,
    })),
  ];
}

function istInput(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const get = (key) => parts.find((part) => part.type === key)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function istIso(input) {
  if (!input) return '';
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(input)
    ? `${input}:00+05:30`
    : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(input)
      ? `${input}+05:30`
      : input;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function previewProductUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname.includes('.') && !url.username && !url.password
      && !/^\d+\.\d+\.\d+\.\d+$/.test(url.hostname) && !url.hostname.startsWith('[')
      && url.hostname !== 'localhost' && !url.hostname.endsWith('.local')
      ? url.toString() : null;
  } catch { return null; }
}

// Mirrors inspectAsset in firebase/functions/mapAds.js. The top banner is one
// full-width header strip with the sponsor's text inside the image.
export function imageShapeProblem(placement, { width, height }) {
  if (placement === 'top') {
    const ratio = width / height;
    return width >= 720 && width <= 2000 && height <= 900 && ratio >= 2.5 && ratio <= 5
      ? '' : `Top banner must be a wide strip: 720–2000 px wide, 2.5:1 to 5:1 (e.g. 1440 × 400). Yours is ${width} × ${height}.`;
  }
  return width >= 40 && height >= 20 && width <= 600 && height <= 400
    ? '' : `Auto tag must be between 40 × 20 and 600 × 400 px. Yours is ${width} × ${height}.`;
}

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve({ width: image.naturalWidth, height: image.naturalHeight }); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image could not be decoded')); };
    image.src = url;
  });
}

function ImagePreview({ path, alt }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let live = true;
    setUrl('');
    if (path) mapAdImageUrl(path).then((value) => { if (live) setUrl(value); }).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return url ? <img src={url} alt={alt} /> : <span className="map-ad-image-empty">{path ? 'Image unavailable' : 'Upload creative'}</span>;
}

export default function MapAds({ isSuperAdmin = false }) {
  const { t } = useLang();
  const [campaigns, setCampaigns] = useState([]);
  const [runtime, setRuntime] = useState({ enabled: false });
  const [selectedId, setSelectedId] = useState(null);
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [heading, setHeading] = useState('north');
  const [towns, setTowns] = useState(() => townOptions({}));

  useEffect(() => listenPlatformConfig((platform) => setTowns(townOptions(platform))), []);

  useEffect(() => {
    const offCampaigns = listenMapAdCampaigns((items) => {
      setCampaigns(items.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
      setLoaded(true);
      setError('');
    }, (err) => { setError(err.message); setLoaded(true); });
    const offRuntime = listenMapAdRuntime(setRuntime, (err) => setError(err.message));
    return () => { offCampaigns(); offRuntime(); };
  }, []);

  const selected = campaigns.find((item) => item.id === selectedId);
  const formDisabled = !isSuperAdmin || !!busy || selected?.status === 'archived';
  const dirty = useMemo(() => {
    if (!selected) return JSON.stringify(form) !== JSON.stringify(EMPTY);
    const baseline = formFromDraft(selected.draft);
    return JSON.stringify(form) !== JSON.stringify(baseline);
  }, [form, selected]);

  // Keep the save/publish version in step with the server when this campaign
  // changes elsewhere (e.g. paused because another campaign was published).
  useEffect(() => {
    if (selected && !dirty && typeof selected.version === 'number') setVersion(selected.version);
  }, [selected, dirty]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const everywhere = SCREENS.every(([key]) => form[key]);
  const setEverywhere = (on) => setForm((current) => ({ ...current, ...Object.fromEntries(SCREENS.map(([key]) => [key, on])) }));
  const toggleTown = (id, on) => setForm((current) => ({
    ...current,
    townIds: on ? [...new Set([...current.townIds, id])].sort() : current.townIds.filter((townId) => townId !== id),
  }));
  const knownTownIds = new Set(towns.map((town) => town.id));
  const missingTowns = form.townIds.filter((id) => !knownTownIds.has(id));
  const loadCampaign = (item) => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    setSelectedId(item?.id || null);
    setVersion(item?.version || 0);
    setForm(item ? formFromDraft(item.draft) : { ...EMPTY });
  };

  const payload = (values = form) => ({ ...values, startAt: istIso(values.startAt), endAt: istIso(values.endAt) });
  const handleError = (err) => {
    const message = err?.message || 'Something went wrong';
    toast.error(message);
    if (err?.code === 'functions/aborted') setError('This campaign changed in another session. Select it again to reload.');
  };

  const save = async () => {
    if (!isSuperAdmin || busy) return null;
    setBusy('save');
    try {
      const result = await saveMapAdDraft({ id: selectedId || undefined, expectedVersion: version, draft: payload() });
      setSelectedId(result.id);
      setVersion(result.version);
      toast.success('Draft saved');
      return result;
    } catch (err) { handleError(err); return null; } finally { setBusy(''); }
  };

  const upload = async (placement, file) => {
    if (!file || formDisabled) return;
    if (!selectedId && !form.name.trim()) {
      toast.error('Enter an internal campaign name before uploading creatives');
      return;
    }
    if (!['image/png', 'image/webp'].includes(file.type)) { toast.error('Use PNG or WebP'); return; }
    const max = placement === 'top' ? 350 * 1024 : 120 * 1024;
    if (file.size > max) { toast.error(`Image must be under ${Math.floor(max / 1024)} KB`); return; }
    setBusy(`upload-${placement}`);
    try {
      const dimensions = await readImageSize(file);
      const problem = imageShapeProblem(placement, dimensions);
      if (problem) throw new Error(problem);
      let id = selectedId;
      let nextVersion = version;
      if (!id) {
        const first = await saveMapAdDraft({ expectedVersion: 0, draft: payload() });
        id = first.id;
        nextVersion = first.version;
        setSelectedId(id);
        setVersion(nextVersion);
      }
      const path = await uploadMapAdImage(id, placement, file);
      const next = { ...form, [`${placement}Path`]: path };
      const result = await saveMapAdDraft({ id, expectedVersion: nextVersion, draft: payload(next) });
      setForm(next);
      setVersion(result.version);
      toast.success(`${placement === 'top' ? 'Top banner' : 'Auto tag'} uploaded and draft saved`);
    } catch (err) { handleError(err); } finally { setBusy(''); }
  };

  const publish = async () => {
    if (!isSuperAdmin || busy) return;
    if (!window.confirm(runtime.enabled
      ? 'Publish this campaign revision? Global ads are ON, so eligible riders on supported app builds may see it immediately.'
      : 'Publish this campaign revision? It will become selected, but global ads remain OFF until you enable them.')) return;
    setBusy('publish');
    try {
      let id = selectedId;
      let nextVersion = version;
      if (dirty || !id) {
        const result = await saveMapAdDraft({ id: id || undefined, expectedVersion: nextVersion, draft: payload() });
        id = result.id;
        nextVersion = result.version;
        setSelectedId(id);
        setVersion(nextVersion);
      }
      const result = await publishMapAd({ id, expectedVersion: nextVersion });
      setVersion(result.version);
      toast.success(runtime.enabled ? 'Campaign published and eligible to display' : 'Published. Enable global ads when ready.');
    } catch (err) { handleError(err); } finally { setBusy(''); }
  };

  const campaignAction = async (action) => {
    if (!selectedId || !isSuperAdmin || busy) return;
    if (dirty && !window.confirm('You have unsaved edits. This action will not save them. Continue?')) return;
    if (!window.confirm(`${action === 'pause' ? 'Pause' : action === 'resume' ? 'Resume' : 'Archive'} this campaign?`)) return;
    setBusy(action);
    try {
      await setMapAdCampaignState({ id: selectedId, action });
      if (dirty && selected) setForm(formFromDraft(selected.draft));
      setVersion((current) => current + 1);
      toast.success(`Campaign ${action === 'archive' ? 'archived' : action === 'pause' ? 'paused' : 'resumed'}`);
    } catch (err) { handleError(err); } finally { setBusy(''); }
  };

  const toggleGlobal = async () => {
    if (!isSuperAdmin || busy) return;
    const next = !runtime.enabled;
    if (next && !window.confirm('Enable live map ads for passengers using supported app builds?')) return;
    setBusy('global');
    try { await setMapAdsEnabled(next); toast.success(next ? 'Map ads enabled' : 'All map ads turned off'); }
    catch (err) { handleError(err); } finally { setBusy(''); }
  };

  return (
    <div className="map-ads-page">
      <div className="map-ads-hero">
        <div><span className="map-ads-eyebrow">{t('CAMPAIGN CONTROL')}</span><h2>{t('Map Ads')}</h2><p>{t('Manage the tappable top banner and the sponsored tag behind the auto.')}</p></div>
        <div className="map-ads-global">
          <div><strong>{t(runtime.enabled ? 'Ads enabled' : 'All ads off')}</strong><small>{t(runtime.enabled ? 'Eligible campaigns can appear in supported passenger builds' : 'Safe default · no campaign is shown')}</small></div>
          <button type="button" role="switch" aria-checked={runtime.enabled === true} aria-label="Enable all map ads" className={`map-ads-switch ${runtime.enabled ? 'on' : ''}`} onClick={toggleGlobal} disabled={!isSuperAdmin || !!busy}><span /></button>
        </div>
      </div>

      {!isSuperAdmin && <div className="map-ads-note">{t('Reviewer access is read only. A Super Admin with MFA is required to change campaigns.')}</div>}
      {error && <div className="map-ads-error" role="alert">{error}</div>}
      <div className="map-ads-layout">
        <aside className="map-ads-list card">
          <div className="map-ads-list-head"><h3>{t('Campaigns')}</h3>{isSuperAdmin && <button className="btn btn-ghost btn-sm" type="button" onClick={() => loadCampaign(null)}>+ {t('New')}</button>}</div>
          {!loaded && <p className="muted">Loading campaigns…</p>}
          {loaded && campaigns.length === 0 && <p className="muted">No campaigns yet. Create a draft to get started.</p>}
          {campaigns.map((item) => <button type="button" key={item.id} className={`map-ads-list-item ${selectedId === item.id ? 'selected' : ''}`} onClick={() => loadCampaign(item)}>
            <strong>{item.name || 'Untitled campaign'}</strong><span>{item.sponsor || 'No sponsor'}</span><em className={`map-ads-state ${item.status}`}>{item.status}</em>
            {runtime.selectedCampaignId === item.id && <small>Selected for display</small>}
          </button>)}
        </aside>

        <section className="map-ads-editor card">
          <div className="map-ads-section-head"><div><h3>{t(selectedId ? 'Edit campaign' : 'New campaign')}</h3><p>{t('Save changes as a draft. Publish is a separate action.')}</p></div>{selected && <span className={`map-ads-state ${selected.status}`}>{t(selected.status)}</span>}</div>
          {selected?.hasUnpublishedChanges && <div className="map-ads-note">{t('Saved draft changes are not live until you publish again.')}</div>}
          <div className="map-ads-fields">
            <label>{t('Internal campaign name')}<input value={form.name} maxLength={80} onChange={(e) => change('name', e.target.value)} disabled={formDisabled} placeholder="Local Mart · October" /></label>
            <label>{t('Sponsor shown to riders')}<input value={form.sponsor} maxLength={60} onChange={(e) => change('sponsor', e.target.value)} disabled={formDisabled} placeholder="Local Mart" /></label>
            <label>{t('Banner text (read aloud)')}<input value={form.headline} maxLength={90} onChange={(e) => change('headline', e.target.value)} disabled={formDisabled} placeholder="This is not just a drink. Let's go!" /><small>{t('Type the words that are in the banner image, for screen readers. Not drawn on the banner.')}</small></label>
            <label>{t('Product destination · HTTPS')}<input type="url" value={form.productUrl} maxLength={1500} onChange={(e) => change('productUrl', e.target.value)} disabled={formDisabled} placeholder="https://example.com/product" /><small>{t('Opens when a rider taps the top banner.')}</small></label>
          </div>
          {previewProductUrl(form.productUrl) && <a className="map-ads-test-link" href={previewProductUrl(form.productUrl)} target="_blank" rel="noopener noreferrer">Test product link ↗</a>}

          <div className="map-ads-options"><strong>{t('Placements')}</strong><label><input type="checkbox" checked={form.topEnabled} onChange={(e) => change('topEnabled', e.target.checked)} disabled={formDisabled} /> {t('Tappable top banner')}</label><label><input type="checkbox" checked={form.tagEnabled} onChange={(e) => change('tagEnabled', e.target.checked)} disabled={formDisabled} /> {t('Auto-tail tag')}</label></div>
          <div className="map-ads-uploads">
            <div><strong>{t('Top banner image')}</strong><small>{t('One full-width image with the text inside it, shown across the top of the map just below the phone’s clock bar. Use the whole image; fade the bottom edge so it blends into the map.')}</small><small>PNG/WebP · max 350 KB · 720–2000 px wide · 2.5:1 to 5:1 · best 1440 × 400</small><div className="map-ads-upload-preview top"><ImagePreview path={form.topPath} alt="Top banner preview" /></div>{!formDisabled && <label className="map-ads-file">{t('Upload top banner')}<input type="file" accept="image/png,image/webp" disabled={!!busy} onChange={(e) => { upload('top', e.target.files?.[0]); e.target.value = ''; }} /></label>}</div>
            <div><strong>{t('Auto tag image')}</strong><small>PNG/WebP · max 120 KB · max 600 × 400 px</small><div className="map-ads-upload-preview tag"><ImagePreview path={form.tagPath} alt="Auto-tail tag preview" /></div>{!formDisabled && <label className="map-ads-file">{t('Upload auto tag')}<input type="file" accept="image/png,image/webp" disabled={!!busy} onChange={(e) => { upload('tag', e.target.files?.[0]); e.target.value = ''; }} /></label>}</div>
          </div>

          <div className="map-ads-options map-ads-options-stack"><strong>{t('Where to show')}</strong>
            <label><input type="checkbox" checked={everywhere} onChange={(e) => setEverywhere(e.target.checked)} disabled={formDisabled} /> {t('Everywhere')}</label>
            {SCREENS.map(([key, label]) => <label key={key}><input type="checkbox" checked={form[key]} onChange={(e) => change(key, e.target.checked)} disabled={formDisabled} /> {t(label)}</label>)}
            {form.tagEnabled && !form.pickupEnabled && !form.tripEnabled && <small className="map-ads-hint">{t('The auto tag shows only on Approaching pickup or During trip.')}</small>}
          </div>
          <div className="map-ads-options map-ads-options-stack"><strong>{t('Show in towns')}</strong>
            <label><input type="checkbox" checked={form.allTowns} onChange={(e) => change('allTowns', e.target.checked)} disabled={formDisabled} /> {t('All towns')}</label>
            {!form.allTowns && towns.map((town) => <label key={town.id}><input type="checkbox" checked={form.townIds.includes(town.id)} onChange={(e) => toggleTown(town.id, e.target.checked)} disabled={formDisabled} /> {town.name}{!town.active && ` (${t('switched off')})`}</label>)}
            {!form.allTowns && missingTowns.map((id) => <label key={id}><input type="checkbox" checked onChange={() => toggleTown(id, false)} disabled={formDisabled} /> {id} ({t('town removed from Settings')})</label>)}
            {!form.allTowns && form.townIds.length === 0 && <small className="map-ads-hint">{t('Tick at least one town.')}</small>}
          </div>
          <div className="map-ads-fields"><label>{t('Starts · IST')}<input type="datetime-local" value={form.startAt} onChange={(e) => change('startAt', e.target.value)} disabled={formDisabled} /></label><label>{t('Ends · IST')}<input type="datetime-local" value={form.endAt} onChange={(e) => change('endAt', e.target.value)} disabled={formDisabled} /></label></div>

          {isSuperAdmin && <div className="map-ads-actions"><button type="button" className="btn btn-ghost" onClick={save} disabled={!!busy || selected?.status === 'archived'}>{t(busy === 'save' ? 'Saving…' : 'Save draft')}</button><button type="button" className="btn btn-green" onClick={publish} disabled={!!busy || selected?.status === 'archived'}>{t(busy === 'publish' ? 'Publishing…' : 'Publish campaign')}</button>{selected?.status === 'published' && <button type="button" className="btn btn-ghost" onClick={() => campaignAction('pause')} disabled={!!busy}>{t('Pause')}</button>}{selected?.status === 'paused' && <button type="button" className="btn btn-ghost" onClick={() => campaignAction('resume')} disabled={!!busy}>{t('Resume')}</button>}{selected && selected.status !== 'archived' && <button type="button" className="btn btn-ghost" onClick={() => campaignAction('archive')} disabled={!!busy}>{t('Archive')}</button>}</div>}
        </section>

        <aside className="map-ads-preview card"><div className="map-ads-section-head"><div><h3>{t('Live preview')}</h3><p>{t('Conceptual passenger placement')}</p></div></div><div className="map-ads-phone">
          <div className="map-ads-phone-status">9:41 <span>● 5G ▰</span></div>
          {form.topEnabled
            ? <div className="map-ads-phone-hero" title={form.headline || form.sponsor || 'Top banner'}><ImagePreview path={form.topPath} alt={form.headline || form.sponsor || 'Top banner'} /><span className="map-ads-ad-mark">Ad</span></div>
            : <div className="map-ads-phone-top">MyTownly <span>Help</span></div>}
          <div className="map-ads-phone-map"><div className="map-ads-route" /><div className="map-ads-pickup">●<small>Pickup</small></div><div className={`map-ads-marker ${heading}`}><span className="map-ads-auto" aria-label="Auto viewed from above"><span className="map-ads-auto-front" /><span className="map-ads-auto-roof" /></span>{form.tagEnabled && <span className="map-ads-tail"><span className="map-ads-tail-image"><ImagePreview path={form.tagPath} alt="Auto tag" /></span><small>Ad</small></span>}</div></div>
          <div className="map-ads-phone-sheet"><strong>Anna arriving in <em>6 min</em></strong><span>1.8 km away · Ramesh</span><button type="button" disabled>Call Anna</button></div>
        </div><div className="map-ads-heading"><span>Auto heading</span>{['north', 'east', 'south', 'west'].map((dir) => <button type="button" key={dir} className={heading === dir ? 'active' : ''} onClick={() => setHeading(dir)}>{dir}</button>)}</div><p className="muted map-ads-preview-note">The banner spans the top of the map; tapping it opens the product link. The tag follows the auto's rear while the logo stays upright. Both carry a small "Ad" mark. This preview does not publish ads.</p></aside>
      </div>
    </div>
  );
}
